import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Pill, Stethoscope, Activity, PlusCircle, Clock, CheckCircle, Siren, Calendar } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config';

const Home = () => {
    const navigate = useNavigate();
    const [systemStatus, setSystemStatus] = useState('checking');
    const [currentTime, setCurrentTime] = useState(new Date());

    const [stats, setStats] = useState({
        patients: 0,
        doctors: 0,
        totalRefills: 0,
        overdue: 0,
        dueToday: 0,
        future: 0,
        priorityItems: [],
        expiringItems: []
    });

    // API_URL is now managed in src/config.js
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const loadDashboardData = async () => {
            try {
                const [patientsRes, doctorsRes, refillsRes, notificationsRes, logsRes] = await Promise.all([
                    axios.get(`${API_URL}?action=get_patients`),
                    axios.get(`${API_URL}?action=get_doctors`),
                    axios.get(`${API_URL}?action=get_refills`),
                    axios.get(`${API_URL}?action=get_notifications`),
                    axios.get(`${API_URL}?action=get_logs`)
                ]);

                const today = new Date();
                today.setHours(0, 0, 0, 0);

                let overdue = 0;
                let dueToday = 0;
                let future = 0;

                const refills = Array.isArray(refillsRes.data) ? refillsRes.data : [];

                // Logic for Priority List
                const priorityList = refills.filter(item => {
                    const expDate = new Date(item.expiration_date);
                    expDate.setHours(0, 0, 0, 0);

                    if (expDate < today) {
                        overdue++;
                        return true;
                    }
                    if (expDate.getTime() === today.getTime()) {
                        dueToday++;
                        return true;
                    }
                    return false;
                }).slice(0, 10);

                setStats({
                    patients: Array.isArray(patientsRes.data) ? patientsRes.data.length : 0,
                    doctors: Array.isArray(doctorsRes.data) ? doctorsRes.data.length : 0,
                    totalRefills: refills.length,
                    overdue,
                    dueToday,
                    future,
                    priorityItems: priorityList,
                    expiringItems: Array.isArray(notificationsRes.data) ? notificationsRes.data.slice(0, 10) : [],
                    recentActivity: Array.isArray(logsRes.data) ? logsRes.data.slice(0, 15) : []
                });

                setSystemStatus('online');
            } catch (error) {
                console.error("Dashboard Load Error:", error);
                setSystemStatus('offline');
            }
        };

        loadDashboardData();
    }, []);

    const getGreeting = () => {
        const hour = currentTime.getHours();
        if (hour < 12) return "Good Morning";
        if (hour < 18) return "Good Afternoon";
        return "Good Evening";
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === '0000-00-00') return '-';
        return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    return (
        <div className="flex flex-col h-full gap-4 overflow-hidden">

            {/* 1. Header Banner (Green Theme) */}
            <div className="bg-gradient-to-r from-green-800 to-teal-600 rounded-xl p-5 text-white shadow-md relative overflow-hidden shrink-0">
                <div className="absolute top-0 right-0 -mr-10 -mt-10 w-40 h-40 bg-white opacity-10 rounded-full"></div>
                <div className="flex justify-between items-end relative z-10">
                    <div>
                        <p className="text-green-100 text-[10px] font-bold uppercase tracking-wide mb-1 flex items-center gap-2">
                            <Clock size={12} /> {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} • {currentTime.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                        </p>
                        <h1 className="text-xl font-bold">{getGreeting()}, Admin</h1>
                    </div>
                    <div className={`px-2 py-1 rounded backdrop-blur-md border flex items-center gap-2 shadow-sm ${systemStatus === 'online' ? 'bg-emerald-500/20 border-emerald-400/30 text-emerald-50' : 'bg-red-500/20 border-red-400/30 text-red-50'}`}>
                        <div className="relative">
                            <div className={`h-2 w-2 rounded-full ${systemStatus === 'online' ? 'bg-emerald-400' : 'bg-red-400'}`}></div>
                            <div className={`absolute top-0 left-0 h-2 w-2 rounded-full animate-ping ${systemStatus === 'online' ? 'bg-emerald-400' : 'bg-red-400'}`}></div>
                        </div>
                        <span className="text-[10px] font-bold uppercase">{systemStatus === 'online' ? 'System Online' : 'Offline'}</span>
                    </div>
                </div>
            </div>

            {/* 2. Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 shrink-0">

                {/* Patients (Green) */}
                <div onClick={() => navigate('/patients')} className="bg-white p-3 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition cursor-pointer flex justify-between items-center group">
                    <div>
                        <h3 className="text-2xl font-bold text-gray-800">{stats.patients}</h3>
                        <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Patients</p>
                    </div>
                    <div className="p-2 bg-green-50 text-green-600 rounded-lg group-hover:bg-green-600 group-hover:text-white transition"><Users size={18} /></div>
                </div>

                {/* Doctors (Purple - kept distinct) */}
                <div onClick={() => navigate('/doctors')} className="bg-white p-3 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition cursor-pointer flex justify-between items-center group">
                    <div>
                        <h3 className="text-2xl font-bold text-gray-800">{stats.doctors}</h3>
                        <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Doctors</p>
                    </div>
                    <div className="p-2 bg-purple-50 text-purple-600 rounded-lg group-hover:bg-purple-600 group-hover:text-white transition"><Stethoscope size={18} /></div>
                </div>

                {/* Refills (Yellow/Red - kept standard for warnings) */}
                <div onClick={() => navigate('/refills')} className="bg-white p-3 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition cursor-pointer group relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-1 h-full bg-yellow-400"></div>
                    <div className="flex justify-between items-center mb-1"><h3 className="text-xl font-bold text-gray-800">{stats.totalRefills}</h3><Pill size={18} className="text-yellow-600" /></div>
                    <div className="flex justify-between text-[10px] mt-1"><span className="text-gray-500 font-bold uppercase tracking-wider">Pending RX</span><span className="text-red-600 font-bold">{stats.overdue} Overdue</span></div>
                </div>
            </div>

            {/* 3. Bottom Section: 3 Columns */}
            <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-3 gap-3">

                {/* Col 1: Quick Actions (Updated to Green Theme) */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col">
                    <h3 className="font-bold text-gray-700 mb-3 text-xs uppercase tracking-wider flex items-center gap-2"><Activity size={14} /> Quick Access</h3>
                    <div className="grid grid-cols-2 gap-3 h-full">

                        {/* Register Patient (Green) */}
                        <button onClick={() => navigate('/patients')} className="flex flex-col items-center justify-center p-2 border border-dashed border-green-200 rounded-lg hover:bg-green-50 hover:border-green-400 transition group text-center h-full">
                            <div className="bg-green-100 text-green-600 p-2 rounded-full mb-2 group-hover:bg-green-600 group-hover:text-white transition"><PlusCircle size={20} /></div>
                            <span className="font-bold text-xs text-gray-700">Register</span>
                            <span className="text-[10px] text-gray-400">New Patient</span>
                        </button>

                        {/* Refill (Green/Teal) */}
                        <button onClick={() => navigate('/refills')} className="flex flex-col items-center justify-center p-2 border border-dashed border-teal-200 rounded-lg hover:bg-teal-50 hover:border-teal-400 transition group text-center h-full">
                            <div className="bg-teal-100 text-teal-600 p-2 rounded-full mb-2 group-hover:bg-teal-600 group-hover:text-white transition"><Pill size={20} /></div>
                            <span className="font-bold text-xs text-gray-700">Refill</span>
                            <span className="text-[10px] text-gray-400">Requirement</span>
                        </button>
                    </div>
                </div>

                {/* Col 2: Priority List */}
                <div className="bg-white rounded-xl shadow-sm border border-red-100 flex flex-col overflow-hidden">
                    <div className="p-3 border-b border-red-50 bg-red-50/30 flex justify-between items-center shrink-0">
                        <h3 className="font-bold text-red-700 text-xs uppercase tracking-wider flex items-center gap-2">
                            <Siren size={14} /> Priority List
                        </h3>
                        <button onClick={() => navigate('/priority')} className="text-[10px] text-red-600 hover:underline font-bold">View All</button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 custom-scrollbar">
                        {stats.priorityItems.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-gray-400 text-xs">
                                <CheckCircle size={20} className="text-green-400 mb-1" /><span>All clear!</span>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {stats.priorityItems.map((item, i) => (
                                    <div
                                        key={i}
                                        className="p-2 border rounded bg-white hover:bg-red-50 cursor-pointer"
                                        onClick={() => navigate(`/priority/${item.patient_id}`)}
                                    >
                                        <div className="flex justify-between">
                                            <span className="text-xs font-bold text-gray-700">{item.drug_name}</span>
                                            <span className="text-[10px] font-bold text-red-600">{formatDate(item.expiration_date)}</span>
                                        </div>
                                        <p className="text-[10px] text-gray-500 truncate">{item.full_name}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Col 3: Expiring Soon */}
                <div className="bg-white rounded-xl shadow-sm border border-orange-100 flex flex-col overflow-hidden">
                    <div className="p-3 border-b border-orange-50 bg-orange-50/30 flex justify-between items-center shrink-0">
                        <h3 className="font-bold text-orange-700 text-xs uppercase tracking-wider flex items-center gap-2">
                            <Clock size={14} /> Expiring Soon
                        </h3>
                        <span className="text-[10px] font-bold text-orange-500 bg-white px-2 py-0.5 rounded border border-orange-200">7 Days</span>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 custom-scrollbar">
                        {stats.expiringItems.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-gray-400 text-xs">
                                <Calendar size={20} className="text-gray-300 mb-1" /><span>No alerts.</span>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {stats.expiringItems.map((item, i) => (
                                    <div
                                        key={i}
                                        className="p-2 border rounded bg-white hover:bg-orange-50 cursor-pointer flex items-center justify-between"
                                        onClick={() => navigate(`/priority/${item.patient_id}`)}
                                    >
                                        <div>
                                            <p className="text-xs font-bold text-gray-800">{item.drug_name}</p>
                                            <p className="text-[10px] text-gray-500 truncate">{item.full_name}</p>
                                        </div>
                                        <div className="text-[10px] font-bold text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded">
                                            {item.days_left < 0 ? 'Expired' : `${item.days_left}d left`}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Home;